import { maintenance, tradesmen, messages as messagesApi, payments, documents } from '../api';
import { createDataTable, type Column } from '../components/table';
import { showModal } from '../components/modal';
import { showToast } from '../components/toast';
import { navigate } from '../router';
import type { MaintenanceDetail, QuoteRequest, Message, Tradesman } from '../types';

function fmt(n: number): string {
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 2 }).format(n);
}

export async function maintenanceDetailPage(params: Record<string, string>): Promise<HTMLElement> {
    const { propertyId, issueId } = params;
    const page = document.createElement('div');

    let detail: MaintenanceDetail;
    try {
        detail = await maintenance.detail(propertyId, issueId);
    } catch {
        page.innerHTML = '<div class="text-red-500 p-8">Error loading maintenance issue. It may not exist.</div>';
        return page;
    }

    // API returns 'maintenance' key and 'quote_requests' key
    const { maintenance: issue, property, quote_requests: quotes, assigned_tradesmen, messages: msgs } = detail;

    // ── Header ──────────────────────────────────────────────────────────────────
    const header = document.createElement('div');
    header.className = 'page-header';
    header.innerHTML = `
      <div>
        <a href="#/properties/${propertyId}" class="text-sm text-gray-400 hover:text-primary-600 mb-1 inline-block">&larr; ${property.address || propertyId}</a>
        <h1 class="text-2xl font-bold text-gray-900">${issue.title || issue.description || 'Maintenance Issue'}</h1>
        <div class="flex gap-2 mt-2">
          <span class="badge priority-${issue.priority}">${issue.priority}</span>
          <span class="badge badge-${issue.status === 'completed' ? 'green' : issue.status === 'in-progress' ? 'yellow' : 'gray'}">${issue.status}</span>
          ${issue.source ? `<span class="badge badge-gray">${issue.source}</span>` : ''}
        </div>
      </div>
      <div class="flex gap-2 flex-wrap">
        <button class="btn-outline" id="edit-issue-btn">Edit</button>
        ${issue.status !== 'completed' ? '<button class="btn-primary bg-green-600 hover:bg-green-700" id="complete-issue-btn">Complete &amp; Record Cost</button>' : ''}
        <button class="btn-primary" id="send-tradesmen-btn">Send to Tradesmen</button>
        <button class="btn-outline" id="send-agent-btn">Contact Agent</button>
      </div>
    `;
    page.appendChild(header);

    // ── Details + Property (2-col grid) ─────────────────────────────────────────
    const detailsSection = document.createElement('div');
    detailsSection.className = 'grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6';
    detailsSection.innerHTML = `
      <div class="card">
        <h3 class="font-semibold text-gray-900 mb-4">Issue Details</h3>
        <dl class="space-y-3 text-sm">
          <div class="flex justify-between"><dt class="text-gray-500">Title</dt><dd class="font-medium">${issue.title || '—'}</dd></div>
          <div><dt class="text-gray-500 mb-1">Description</dt><dd class="text-gray-700 whitespace-pre-wrap">${issue.description || '—'}</dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Date Reported</dt><dd class="font-medium">${issue.date_reported || '—'}</dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Priority</dt><dd><span class="badge priority-${issue.priority}">${issue.priority}</span></dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Status</dt><dd><span class="badge badge-${issue.status === 'completed' ? 'green' : issue.status === 'in-progress' ? 'yellow' : 'gray'}">${issue.status}</span></dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Quoted Amount</dt><dd class="font-medium">${issue.quoted_amount ? fmt(issue.quoted_amount) : '—'}</dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Actual Cost</dt><dd class="font-medium ${issue.actual_amount ? 'text-red-600' : ''}">${issue.actual_amount ? fmt(issue.actual_amount) : '—'}</dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Completed</dt><dd class="font-medium">${issue.completed_date || '—'}</dd></div>
          ${issue.notes ? `<div><dt class="text-gray-500 mb-1">Notes</dt><dd class="text-gray-700 text-xs whitespace-pre-wrap">${issue.notes}</dd></div>` : ''}
          ${issue.media_paths?.length ? `<div><dt class="text-gray-500 mb-1">Attachments (${issue.media_paths.length})</dt><dd class="space-y-1">${
            issue.media_paths.map(p => {
              const name = p.split('/').pop() || p;
              const icon = name.match(/\.pdf$/i) ? '📄' : name.match(/\.(jpe?g|png|gif|webp|heic)$/i) ? '🖼️' : '📎';
              return `<button class="attachment-link flex items-center gap-2 text-sm text-primary-600 hover:underline w-full text-left" data-blob-path="${p}" data-prop-id="${propertyId}">${icon} ${name}</button>`;
            }).join('')
          }</dd></div>` : ''}
        </dl>
      </div>

      <div class="card">
        <h3 class="font-semibold text-gray-900 mb-4">Property &amp; Agent</h3>
        <dl class="space-y-3 text-sm">
          <div class="flex justify-between"><dt class="text-gray-500">Property</dt><dd class="font-medium"><a href="#/properties/${property.id}" class="text-primary-600 hover:underline">${property.address}${property.suburb ? `, ${property.suburb}` : ''}</a></dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Agent</dt><dd class="font-medium">${property.agent_name || '—'}</dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Agent Phone</dt><dd class="font-medium">${property.agent_phone ? `<a href="tel:${property.agent_phone}" class="text-primary-600 hover:underline">${property.agent_phone}</a>` : '—'}</dd></div>
          <div class="flex justify-between"><dt class="text-gray-500">Agent Email</dt><dd class="font-medium">${property.agent_emails?.length ? `<a href="mailto:${property.agent_emails[0]}" class="text-primary-600 hover:underline">${property.agent_emails[0]}</a>` : '—'}</dd></div>
        </dl>

        <h3 class="font-semibold text-gray-900 mt-6 mb-3">Assigned Tradesmen</h3>
        ${assigned_tradesmen.length === 0
        ? '<p class="text-sm text-gray-400">No tradesmen assigned yet. Use "Send to Tradesmen" above.</p>'
        : `<div class="space-y-2">${assigned_tradesmen.map(t => `
              <a href="#/tradesmen/${t.id}" class="flex items-center gap-3 p-2 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-gray-900">${t.name}</p>
                  <p class="text-xs text-gray-500">${t.trade}${t.company ? ` · ${t.company}` : ''}${t.phone ? ` · ${t.phone}` : ''}</p>
                </div>
                <span class="badge badge-blue shrink-0">${t.trade}</span>
              </a>`).join('')}</div>`}
      </div>
    `;
    page.appendChild(detailsSection);

    // Wire attachment download links
    detailsSection.addEventListener('click', async (e) => {
        const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.attachment-link');
        if (!btn) return;
        const blobPath = btn.dataset.blobPath!;
        const pid = btn.dataset.propId!;
        btn.textContent = '⏳ Downloading…';
        try {
            const { url } = await documents.download(pid, blobPath);
            const a = document.createElement('a');
            a.href = url;
            a.download = blobPath.split('/').pop() || 'attachment';
            a.click();
        } catch {
            showToast('Failed to download attachment', 'error');
        } finally {
            // Restore original label
            const name = blobPath.split('/').pop() || blobPath;
            const icon = name.match(/\.pdf$/i) ? '📄' : name.match(/\.(jpe?g|png|gif|webp|heic)$/i) ? '🖼️' : '📎';
            btn.textContent = `${icon} ${name}`;
        }
    });

    // ── Quote Requests ───────────────────────────────────────────────────────────
    const quotesSection = document.createElement('div');
    quotesSection.className = 'card mb-6';
    quotesSection.innerHTML = `<h3 class="font-semibold text-gray-900 mb-4">Quote Requests <span class="text-gray-400 font-normal text-sm">(${quotes.length})</span></h3>`;
    const quoteCols: Column<QuoteRequest>[] = [
        { label: 'Tradesman', key: 'tradesman_id' },
        { label: 'Status', render: r => `<span class="badge badge-${r.status === 'accepted' ? 'green' : r.status === 'declined' ? 'red' : r.status === 'responded' ? 'blue' : 'yellow'}">${r.status}</span>` },
        { label: 'Channel', render: r => `<span class="badge badge-gray">${r.channel}</span>` },
        { label: 'Quoted', render: r => r.quoted_amount ? fmt(r.quoted_amount) : '—', className: 'text-right' },
        { label: 'Sent', render: r => r.sent_at ? new Date(r.sent_at).toLocaleDateString('en-AU') : '—' },
        { label: 'Responded', render: r => r.responded_at ? new Date(r.responded_at).toLocaleDateString('en-AU') : '—' },
    ];
    quotesSection.appendChild(createDataTable(quoteCols, quotes, { emptyText: 'No quote requests yet.' }));
    page.appendChild(quotesSection);

    // ── Messages Thread ──────────────────────────────────────────────────────────
    const messagesSection = document.createElement('div');
    messagesSection.className = 'card';
    messagesSection.innerHTML = `<h3 class="font-semibold text-gray-900 mb-4">Messages &amp; Notes <span class="text-gray-400 font-normal text-sm">(${msgs.length})</span></h3>`;
    const msgContainer = document.createElement('div');
    renderMessages(msgContainer, msgs, propertyId, issueId);
    messagesSection.appendChild(msgContainer);
    page.appendChild(messagesSection);

    // ── Wire action buttons ──────────────────────────────────────────────────────
    setTimeout(() => {
        page.querySelector('#edit-issue-btn')?.addEventListener('click', () => {
            showEditMaintenanceModal(propertyId, issueId, issue);
        });
        page.querySelector('#complete-issue-btn')?.addEventListener('click', () => {
            showCompleteMaintenanceModal(propertyId, issueId, issue);
        });
        page.querySelector('#send-tradesmen-btn')?.addEventListener('click', () => {
            showSendToTradesmenModal(propertyId, issueId, issue, async () => {
                navigate(`#/properties/${propertyId}/maintenance/${issueId}`);
            });
        });
        page.querySelector('#send-agent-btn')?.addEventListener('click', () => {
            showSendToAgentModal(propertyId, issueId, property, async () => {
                navigate(`#/properties/${propertyId}/maintenance/${issueId}`);
            });
        });
    });

    return page;
}

function showEditMaintenanceModal(propertyId: string, issueId: string, issue: MaintenanceDetail['maintenance']): void {
    const form = document.createElement('form');
    form.className = 'space-y-4';
    form.innerHTML = `
      <div><label class="label">Title</label><input name="title" type="text" class="input-field" value="${(issue.title || '').replace(/"/g, '&quot;')}"></div>
      <div><label class="label">Description</label><textarea name="description" class="input-field" rows="4">${issue.description || ''}</textarea></div>
      <div class="grid grid-cols-2 gap-4">
        <div>
          <label class="label">Priority</label>
          <select name="priority" class="input-field">
            <option value="low" ${issue.priority === 'low' ? 'selected' : ''}>Low</option>
            <option value="medium" ${issue.priority === 'medium' ? 'selected' : ''}>Medium</option>
            <option value="high" ${issue.priority === 'high' ? 'selected' : ''}>High</option>
            <option value="urgent" ${issue.priority === 'urgent' ? 'selected' : ''}>Urgent</option>
          </select>
        </div>
        <div>
          <label class="label">Status</label>
          <select name="status" class="input-field">
            <option value="open" ${issue.status === 'open' ? 'selected' : ''}>Open</option>
            <option value="in-progress" ${issue.status === 'in-progress' ? 'selected' : ''}>In Progress</option>
            <option value="completed" ${issue.status === 'completed' ? 'selected' : ''}>Completed</option>
          </select>
        </div>
      </div>
      <div class="grid grid-cols-2 gap-4">
        <div><label class="label">Quoted Amount ($)</label><input name="quoted_amount" type="number" step="0.01" class="input-field" value="${issue.quoted_amount || ''}"></div>
        <div><label class="label">Actual Cost ($)</label><input name="actual_amount" type="number" step="0.01" class="input-field" value="${issue.actual_amount || ''}"></div>
      </div>
      <div><label class="label">Notes</label><textarea name="notes" class="input-field" rows="2">${issue.notes || ''}</textarea></div>
      <div class="flex justify-end gap-3"><button type="button" class="btn-outline cancel-btn">Cancel</button><button type="submit" class="btn-primary">Save</button></div>
    `;
    const { close } = showModal('Edit Maintenance Issue', form, { wide: true });
    form.querySelector('.cancel-btn')!.addEventListener('click', close);
    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const fd = new FormData(form);
        try {
            await maintenance.update(propertyId, issueId, {
                title: fd.get('title') as string,
                description: fd.get('description') as string,
                priority: fd.get('priority') as string,
                status: fd.get('status') as string,
                quoted_amount: parseFloat(fd.get('quoted_amount') as string) || 0,
                actual_amount: parseFloat(fd.get('actual_amount') as string) || 0,
                notes: fd.get('notes') as string,
            });
            close();
            showToast('Issue updated', 'success');
            navigate(`#/properties/${propertyId}/maintenance/${issueId}`);
        } catch { showToast('Failed to update', 'error'); }
    });
}

function showCompleteMaintenanceModal(propertyId: string, issueId: string, issue: MaintenanceDetail['maintenance']): void {
    const today = new Date().toISOString().split('T')[0];
    const form = document.createElement('form');
    form.className = 'space-y-4';
    form.innerHTML = `
      <p class="text-sm text-gray-600">Mark this issue as completed and record the repair cost. The cost will be added as an outgoing payment against this property.</p>
      <div class="grid grid-cols-2 gap-4">
        <div><label class="label">Actual Cost ($)</label><input name="actual_amount" type="number" step="0.01" class="input-field" required value="${issue.actual_amount || issue.quoted_amount || ''}"></div>
        <div><label class="label">Completion Date</label><input name="completed_date" type="date" class="input-field" required value="${issue.completed_date || today}"></div>
      </div>
      <div><label class="label">Notes</label><textarea name="notes" class="input-field" rows="2">${issue.notes || ''}</textarea></div>
      <label class="flex items-center gap-2 text-sm text-gray-700">
        <input type="checkbox" name="record_payment" checked class="rounded border-gray-300">
        Record as outgoing payment against property
      </label>
      <div class="flex justify-end gap-3"><button type="button" class="btn-outline cancel-btn">Cancel</button><button type="submit" class="btn-primary bg-green-600 hover:bg-green-700">Complete</button></div>
    `;
    const { close } = showModal('Complete Maintenance Issue', form);
    form.querySelector('.cancel-btn')!.addEventListener('click', close);
    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const fd = new FormData(form);
        const actualAmount = parseFloat(fd.get('actual_amount') as string) || 0;
        const completedDate = fd.get('completed_date') as string;
        const recordPayment = (form.querySelector('input[name="record_payment"]') as HTMLInputElement).checked;
        try {
            await maintenance.update(propertyId, issueId, {
                status: 'completed',
                actual_amount: actualAmount,
                completed_date: completedDate,
                notes: fd.get('notes') as string,
            });
            if (recordPayment && actualAmount > 0) {
                await payments.create(propertyId, {
                    payment_date: completedDate,
                    amount: -actualAmount,
                    type: 'other',
                    reference: `Maintenance: ${issue.title || issue.description || issueId}`,
                    notes: `Repair cost for maintenance issue`,
                    source: 'manual',
                });
            }
            close();
            showToast('Issue completed' + (recordPayment ? ' & payment recorded' : ''), 'success');
            navigate(`#/properties/${propertyId}/maintenance/${issueId}`);
        } catch { showToast('Failed to complete', 'error'); }
    });
}

function _statusBadge(status: string): string {
    if (!status) return '';
    const colors: Record<string, string> = {
        queued: 'badge-yellow', sent: 'badge-blue', delivered: 'badge-green',
        received: 'badge-green', read: 'badge-green', failed: 'badge-red',
        undelivered: 'badge-red', error: 'badge-red',
    };
    const cls = colors[status] || 'badge-gray';
    return `<span class="badge ${cls} text-[10px] ml-1">${status}</span>`;
}

function renderMessages(container: HTMLElement, msgs: Message[], propertyId: string, issueId: string): void {
    container.innerHTML = '';
    const thread = document.createElement('div');
    thread.className = 'space-y-3 mb-4';

    if (msgs.length === 0) {
        thread.innerHTML = '<p class="text-gray-400 text-sm text-center py-6">No messages yet.</p>';
    } else {
        msgs.forEach(m => {
            const isInbound = m.direction === 'inbound';
            const channelIcon = m.channel === 'sms' ? '📱' : '✉️';
            const contactInfo = isInbound ? m.from_address : m.to_address;
            thread.innerHTML += `
              <div class="flex ${isInbound ? 'justify-start' : 'justify-end'}">
                <div class="max-w-[78%] p-3 rounded-lg ${isInbound ? 'bg-gray-100' : 'bg-primary-50 border border-primary-200'}">
                  <div class="flex items-center gap-2 mb-1 flex-wrap">
                    <span class="text-xs font-medium ${isInbound ? 'text-gray-700' : 'text-primary-700'}">${isInbound ? (m.from_address || 'Inbound') : 'You'}</span>
                    <span class="text-xs text-gray-400">${channelIcon} ${m.channel}</span>
                    ${contactInfo ? `<span class="text-xs text-gray-400 font-mono">${contactInfo}</span>` : ''}
                    ${_statusBadge(m.status)}
                  </div>
                  ${m.subject ? `<p class="text-xs font-semibold text-gray-600 mb-1">${m.subject}</p>` : ''}
                  <p class="text-sm text-gray-800 whitespace-pre-wrap">${m.body}</p>
                  <p class="text-xs text-gray-400 mt-1">${new Date(m.created_at).toLocaleString('en-AU')}</p>
                </div>
              </div>
            `;
        });
    }
    container.appendChild(thread);

    const form = document.createElement('form');
    form.className = 'flex gap-2 mt-2';
    form.innerHTML = `
      <input type="text" name="body" class="input-field flex-1" placeholder="Add a note..." required>
      <button type="submit" class="btn-primary whitespace-nowrap">Add Note</button>
    `;
    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const fd = new FormData(form);
        try {
            await messagesApi.create('maintenance', issueId, {
                body: fd.get('body') as string,
                direction: 'outbound',
                channel: 'email',
                related_property_id: propertyId,
                related_maintenance_id: issueId,
            });
            showToast('Note added', 'success');
            const updated = await messagesApi.list('maintenance', issueId);
            renderMessages(container, updated, propertyId, issueId);
        } catch { showToast('Failed to add note', 'error'); }
    });
    container.appendChild(form);
}

async function showSendToTradesmenModal(
    propertyId: string,
    issueId: string,
    issue: MaintenanceDetail['maintenance'],
    onDone: () => Promise<void>,
): Promise<void> {
    let allTradesmen: Tradesman[] = [];
    try {
        allTradesmen = await tradesmen.list();
    } catch {
        showToast('Failed to load tradesmen', 'error');
        return;
    }

    const form = document.createElement('form');
    form.className = 'space-y-4';
    form.innerHTML = `
      <div>
        <label class="label">Select Tradesmen</label>
        <div class="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
          ${allTradesmen.length === 0
        ? '<p class="text-gray-400 text-sm">No tradesmen found. Add them in the Tradesmen section first.</p>'
        : allTradesmen.map(t => `
              <label class="flex items-center gap-2 cursor-pointer hover:bg-gray-100 transition-colors p-1 rounded">
                <input type="checkbox" name="tradesman_ids" value="${t.id}" class="rounded border-gray-300" ${issue.assigned_tradesman_ids?.includes(t.id) ? 'checked' : ''}>
                <span class="text-sm">${t.name} <span class="text-gray-400 text-xs">(${t.trade}${t.phone ? ` · ${t.phone}` : ''}${t.email ? ` · ${t.email}` : ''})</span></span>
              </label>`).join('')}
        </div>
      </div>
      <div>
        <label class="label">Message</label>
        <textarea name="message" class="input-field" rows="4" required>Hi, I have a maintenance issue: ${issue.title || issue.description || ''}. Could you please provide a quote?</textarea>
      </div>
      <div>
        <label class="label">Channel</label>
        <select name="channel" class="input-field">
          <option value="email">Email</option>
          <option value="sms">SMS</option>
        </select>
      </div>
      <div class="flex justify-end gap-3">
        <button type="button" class="btn-outline cancel-btn">Cancel</button>
        <button type="submit" class="btn-primary">Send</button>
      </div>
    `;

    const { close } = showModal('Send to Tradesmen', form, { wide: true });
    form.querySelector('.cancel-btn')!.addEventListener('click', close);
    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const checked = Array.from(form.querySelectorAll<HTMLInputElement>('input[name="tradesman_ids"]:checked'));
        const ids = checked.map(el => el.value);
        if (ids.length === 0) { showToast('Select at least one tradesman', 'error'); return; }
        const fd = new FormData(form);
        try {
            await maintenance.sendToTradesmen(propertyId, issueId, {
                tradesman_ids: ids,
                message: fd.get('message') as string,
                channel: fd.get('channel') as string,
            });
            close();
            showToast('Sent successfully', 'success');
            await onDone();
        } catch { showToast('Failed to send', 'error'); }
    });
}

async function showSendToAgentModal(
    propertyId: string,
    issueId: string,
    property: MaintenanceDetail['property'],
    onDone: () => Promise<void>,
): Promise<void> {
    const hasEmail = property.agent_emails && property.agent_emails.length > 0;
    const hasPhone = !!property.agent_phone;

    const form = document.createElement('form');
    form.className = 'space-y-4';
    form.innerHTML = `
      <p class="text-sm text-gray-500">Send a message to ${property.agent_name || 'the property agent'} regarding this maintenance issue.</p>
      ${!hasEmail && !hasPhone ? '<div class="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm text-yellow-700">⚠ No agent contact info on file for this property.</div>' : ''}
      <div>
        <label class="label">Message</label>
        <textarea name="message" class="input-field" rows="4" required>Hi ${property.agent_name || ''}, I am writing regarding a maintenance issue at ${property.address}. Could you please advise on the next steps?</textarea>
      </div>
      <div>
        <label class="label">Channel</label>
        <select name="channel" class="input-field">
          <option value="email" ${!hasEmail ? 'disabled' : ''}>Email${hasEmail ? ` (${property.agent_emails![0]})` : ' — no email on file'}</option>
          <option value="sms" ${!hasPhone ? 'disabled' : ''}>SMS${hasPhone ? ` (${property.agent_phone})` : ' — no phone on file'}</option>
        </select>
      </div>
      <div class="flex justify-end gap-3">
        <button type="button" class="btn-outline cancel-btn">Cancel</button>
        <button type="submit" class="btn-primary" ${!hasEmail && !hasPhone ? 'disabled' : ''}>Send</button>
      </div>
    `;

    const { close } = showModal('Contact Agent', form);
    form.querySelector('.cancel-btn')!.addEventListener('click', close);
    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const fd = new FormData(form);
        try {
            await maintenance.sendToAgent(propertyId, issueId, {
                message: fd.get('message') as string,
                channel: fd.get('channel') as string,
            });
            close();
            showToast('Message sent to agent', 'success');
            await onDone();
        } catch { showToast('Failed to send to agent', 'error'); }
    });
}

